import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {ReviewThreadComment} from '@github-ui/diff-lines/review-thread-comment'
import {ReviewThread} from '@github-ui/diff-lines/review-thread'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {memo, use, useMemo} from 'react'
import {ThreadHeader} from './ThreadHeader'
import {getFirstComment} from '../../page-data/payloads/thread-previews'
import {useGetThread} from '../../stores/layout-selectors'
import {getThreadPreviewsLineAndFileKeysFromThread} from '../../page-data/loaders/use-thread-previews'
import {ThreadReplyComposer} from './ThreadReplyComposer'
import {useNavigateToThreadComment} from '../../hooks/use-navigate-to-thread-comment'
import {NavigateToCommentButton} from './NavigateToCommentButton'
import {PreviewAuthors} from './PreviewAuthors'
import {isThreadOutdated} from '../../helpers/thread-navigation'
import {CommentsContext} from './CommentsContext'
import {useReviewThreadCollapseState} from '@github-ui/use-review-thread-collapse-state'
import {CommentLayout} from './CommentLayout'

type ThreadPreviewProps = {
  onNavigateToDiffComment?: () => void
  thread: Thread
  selectedReviewId: number | null
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  /** Prefix for comment anchors; combined with prefix from `CommentsContext` */
  commentAnchorPrefix?: string
}

/**
 * Renders a preview of a thread in a pull request.
 * It displays the thread's header, the difflines that the
 * thread is associated with, and the first comment of the thread.
 */
export const ThreadPreview = memo(function ThreadPreviewComponent({
  onNavigateToDiffComment,
  thread,
  selectedReviewId,
  headingLevel,
  commentAnchorPrefix: propsCommentAnchorPrefix,
}: ThreadPreviewProps) {
  const displayComment = useMemo(() => getDisplayComment(thread, selectedReviewId), [thread, selectedReviewId])
  const navigateToComment = useNavigateToThreadComment(thread, onNavigateToDiffComment, displayComment?.databaseId)
  const getThreadFromStore = useGetThread()
  const {tabSize, commentAnchorPrefix: contextCommentAnchorPrefix} = use(CommentsContext)
  const commentAnchorPrefix = `${contextCommentAnchorPrefix ?? ''}${propsCommentAnchorPrefix ?? ''}`

  const {filePath, lineKey} = getThreadPreviewsLineAndFileKeysFromThread(thread)
  const storedThread = getThreadFromStore(Number(thread.id), filePath, lineKey)
  //TODO: if the stored thread is undefined, then add the thread to the store
  const isResolved = storedThread?.isResolved || thread.isResolved || false
  const isOutdated = isThreadOutdated(thread)

  const [isCollapsed, setIsCollapsed] = useReviewThreadCollapseState(thread.id, isResolved)
  const isDockedCommentsRepliesEnabled = isFeatureEnabled('prs_docked_comments_replies')
  let diffLinesForPreview = thread.subject?.diffLines

  // if we don't have a start line, or it is the same as the end line, then we only want to show the diff line
  // the comment was made on.
  if (!thread.subject?.startLine || thread.subject?.startLine === thread.subject?.endLine) {
    diffLinesForPreview = thread.subject?.diffLines?.slice(-1)
  }

  // Memoize the expensive findIndex operation
  const selectedCommentIndex = useMemo(() => {
    const comments = thread.commentsData?.comments ?? []
    return displayComment ? comments.findIndex(c => c.id === displayComment.id) : 0
  }, [thread.commentsData?.comments, displayComment])

  const allComments = thread.commentsData?.comments ?? []
  const previousCommentCount = Math.max(selectedCommentIndex, 0)
  const replyCount = Math.max(allComments.length - previousCommentCount - 1, 0)

  // if the comment returned is a reply, this will be true
  if (!displayComment) return null

  return (
    <CommentLayout
      flushBody /* for diff preview to fill layout */
      header={
        <ThreadHeader
          firstCommentId={displayComment?.databaseId}
          isCollapsed={isCollapsed}
          thread={thread}
          onNavigateToDiffComment={navigateToComment}
          onToggleCollapsed={() => setIsCollapsed(!isCollapsed)}
          headingLevel={headingLevel}
          isResolved={isResolved}
          isOutdated={isOutdated}
          lineKey={lineKey}
          resolutionReason={storedThread?.resolutionReason ?? thread.resolutionReason}
        />
      }
    >
      {!isCollapsed && (
        <>
          {diffLinesForPreview && diffLinesForPreview.length > 0 && (
            <div className="border-bottom borderColor-muted overflow-x-auto">
              <StaticUnifiedDiffPreview
                diffTableClassName="border-0"
                diffLines={diffLinesForPreview}
                className="m-0 border-0"
                tabSize={tabSize || 4}
              />
            </div>
          )}
          {isDockedCommentsRepliesEnabled ? (
            <>
              <ReviewThread
                lineMarkersKey={lineKey}
                batchingEnabled
                commentAnchorPrefix={commentAnchorPrefix}
                disableSuggestedChangesActions
                filePath={filePath}
                gridCellId={`${thread.id}-comments-panel-thread`}
                isInlineComment={false}
                thread={thread}
                shouldLimitHeight={false}
                // built-in reply composer requires DiffStoreProvider to be in the tree, which is only true for files changed
                repliesDisabled
              />
              {thread.viewerCanReply && <ThreadReplyComposer thread={thread} startExpanded={false} />}
            </>
          ) : (
            <>
              {previousCommentCount > 0 && (
                <div className="tmp-px-3 py-2 border-bottom borderColor-muted d-flex flex-items-center gap-2">
                  <PreviewAuthors thread={thread} skipAfterCommentId={displayComment?.databaseId} />
                  <span className="text-small fgColor-muted">
                    {previousCommentCount === 1 ? '1 earlier comment' : `${previousCommentCount} earlier comments`}
                  </span>
                </div>
              )}
              <div>
                <ReviewThreadComment
                  lineMarkersKey={lineKey}
                  key={displayComment?.id}
                  hideActions
                  isInlineComment={false}
                  comment={displayComment}
                  filePath={filePath}
                  index={0}
                  isAnchorable={false}
                  isOutdated={isOutdated}
                  isThreadResolved={isResolved}
                  threadId={thread.id}
                  originalDiffPathUri={thread.originalDiffPathUri}
                  headingLevel="h3"
                  tooltipDirection="sw"
                />
              </div>
              <div className="mb-2 px-2">
                <NavigateToCommentButton
                  thread={thread}
                  displayedCommentId={displayComment?.databaseId}
                  onNavigateToDiffComment={onNavigateToDiffComment}
                  replyCount={replyCount}
                />
              </div>
            </>
          )}
        </>
      )}
    </CommentLayout>
  )
})

/**
 * Gets the comment to display for a thread based on the selected review ID.
 * If there's a review-id filter, returns the last comment in the thread that matches the review.
 * Otherwise, returns the first comment.
 */
function getDisplayComment(thread: Thread, selectedReviewId: number | null): Comment | undefined {
  const firstComment = getFirstComment(thread)

  // If no review-id filter, use first comment
  if (selectedReviewId === null) {
    return firstComment
  }

  // Find the last comment in the thread that matches the review ID
  const threadComments = thread.commentsData?.comments ?? []
  const matchingComments = threadComments.filter(comment => comment.reviewId === selectedReviewId)

  // Return the last matching comment, or fall back to first comment if no matches
  return matchingComments[matchingComments.length - 1] ?? firstComment
}
